import React from 'react';

// Kingdom tier configurations - Enhanced with more buildings and landmarks
const TIER_CONFIG = {
  0: { name: 'Wilderness Camp', biome: 'forest', time: 'morning', buildings: ['🏕️', '🔥'] },
  1: { name: 'Small Settlement', biome: 'forest', time: 'day', buildings: ['🏕️', '🏠', '🏠', '🌲', '🪓'] },
  2: { name: 'Growing Village', biome: 'plains', time: 'day', buildings: ['🏠', '🏠', '🏡', '🏚️', '🌾', '🌳', '🐄'] },
  3: { name: 'Bustling Town', biome: 'plains', time: 'afternoon', buildings: ['🏠', '🏡', '🏘️', '🏪', '⛪', '🌳', '🌲', '⛲', '🐕'] },
  4: { name: 'Prosperous City', biome: 'riverside', time: 'sunset', buildings: ['🏘️', '🏪', '🏛️', '⛪', '🗼', '🌳', '⛲', '🌉', '🚣'] },
  5: { name: 'Grand Kingdom', biome: 'coastal', time: 'sunset', buildings: ['🏰', '🏛️', '⛪', '🏪', '🗼', '🏘️', '⛲', '🌳', '⚓', '🚢'] },
  6: { name: 'Mighty Empire', biome: 'coastal', time: 'day', buildings: ['🏰', '🏛️', '📚', '🏦', '⛪', '🗼', '🏪', '⚓', '🌴', '🎭', '🎪'] },
  7: { name: 'Majestic Realm', biome: 'mountain', time: 'dusk', buildings: ['🏰', '🏰', '🏛️', '📚', '🏦', '⛪', '🗼', '🔭', '❄️', '🦅', '⛷️'] },
  8: { name: 'Legendary Dominion', biome: 'mountain', time: 'night', buildings: ['🏰', '🏯', '🏛️', '📚', '🏦', '⛪', '🗼', '🔮', '✨', '🎆', '🌌'] },
  9: { name: 'Eternal Dynasty', biome: 'magical', time: 'night', buildings: ['🏯', '🏰', '🏛️', '🔮', '📚', '🏦', '✨', '🌟', '💎', '🐉', '🦄'] },
  10: { name: 'Wonder of the World', biome: 'floating', time: 'magical', buildings: ['🌟', '🏯', '🏰', '🏛️', '🔮', '✨', '💫', '👑', '🌈', '🪐', '🛸', '🌌'] },
};

// Tier thresholds for progress calculation
const TIER_THRESHOLDS = [0, 10, 30, 60, 100, 150, 200, 300, 400, 600, 1000];

const TIER_ICONS = ['🏕️', '🏘️', '🏠', '🏙️', '🌆', '🏰', '👑', '⭐', '🌟', '💫', '🌌'];

function KingdomVisual({ tier = 0, population = 0, totalBuildings = 0 }) {
  const config = TIER_CONFIG[tier] || TIER_CONFIG[0];
  const { name, biome, time, buildings } = config;

  // Render weather effects
  const renderWeather = () => {
    switch (biome) {
      case 'mountain':
        return (
          <div className="kv-weather">
            {[...Array(20)].map((_, i) => (
              <span 
                key={i} 
                className="kv-snowflake"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 8}s`,
                  animationDuration: `${6 + Math.random() * 4}s`
                }}
              >❄️</span>
            ))}
            {time === 'night' && (
              <div className="kv-aurora">
                <div className="kv-aurora-wave"></div>
                <div className="kv-aurora-wave"></div>
                <div className="kv-aurora-wave"></div>
              </div>
            )}
          </div>
        );
      case 'magical':
      case 'floating':
        return (
          <div className="kv-weather">
            {[...Array(30)].map((_, i) => (
              <span 
                key={i} 
                className="kv-star"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 50}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              ></span>
            ))}
            {[...Array(15)].map((_, i) => (
              <span 
                key={`p${i}`} 
                className="kv-magic-particle"
                style={{
                  left: `${10 + Math.random() * 80}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  background: ['#e040fb', '#00e5ff', '#76ff03', '#ffeb3b'][Math.floor(Math.random() * 4)]
                }}
              ></span>
            ))}
          </div>
        );
      case 'coastal':
        return (
          <div className="kv-weather">
            {[...Array(3)].map((_, i) => (
              <span
                key={i}
                className="kv-bird"
                style={{
                  top: `${15 + i * 10}%`,
                  animationDelay: `${i * -7}s`
                }}
              >🐦</span>
            ))}
          </div>
        );
      case 'forest':
        return (
          <div className="kv-weather">
            {/* Fireflies at dusk/night */}
            {['dusk', 'night'].includes(time) && [...Array(12)].map((_, i) => (
              <span
                key={`ff${i}`}
                className="kv-firefly"
                style={{
                  left: `${10 + (i * 7) % 80}%`,
                  bottom: `${30 + (i * 5) % 40}%`,
                  animationDelay: `${(i * 0.7) % 8}s`
                }}
              />
            ))}
            {/* Falling leaves */}
            {['🍂', '🍁', '🌿', '🍃', '🍂', '🍁'].map((leaf, i) => (
              <span
                key={`leaf${i}`}
                className="kv-leaf"
                style={{
                  left: `${(i * 17) % 100}%`,
                  animationDelay: `${(i * 1.5) % 10}s`,
                  animationDuration: `${8 + (i % 4)}s`
                }}
              >{leaf}</span>
            ))}
          </div>
        );
      case 'plains':
        return (
          <div className="kv-weather">
            {/* Butterflies */}
            {['🦋', '🦋', '🦋', '🦋'].map((_, i) => (
              <span
                key={`bf${i}`}
                className="kv-butterfly"
                style={{
                  left: `${10 + (i * 20) % 70}%`,
                  top: `${30 + (i * 10) % 30}%`,
                  animationDelay: `${i * 3}s`
                }}
              >🦋</span>
            ))}
          </div>
        );
      case 'riverside':
        return (
          <div className="kv-weather">
            {/* Fish jumping */}
            {[...Array(3)].map((_, i) => (
              <span
                key={`fish${i}`}
                className="kv-fish"
                style={{
                  left: `${20 + i * 25}%`,
                  animationDelay: `${i * 2}s`
                }}
              >🐟</span>
            ))}
          </div>
        );
      default:
        // Shooting stars for night/magical times
        if (['night', 'magical', 'dusk'].includes(time)) {
          return (
            <div className="kv-weather">
              {[...Array(4)].map((_, i) => (
                <span
                  key={`star${i}`}
                  className="kv-shooting-star"
                  style={{
                    left: `${10 + i * 20}%`,
                    top: `${5 + (i * 8) % 25}%`,
                    animationDelay: `${i * 4}s`
                  }}
                />
              ))}
            </div>
          );
        }
        return null;
    }
  };

  // Render celestial bodies
  const renderCelestial = () => {
    switch (time) {
      case 'morning':
        return <span className="kv-sun rising">☀️</span>;
      case 'day':
      case 'afternoon':
        return <span className="kv-sun" style={{ top: '15%', right: '20%' }}>☀️</span>;
      case 'sunset':
        return <span className="kv-sun setting">🌅</span>;
      case 'dusk':
        return (
          <>
            <span className="kv-sun setting" style={{ right: '10%', opacity: 0.7 }}>🌅</span>
            <span className="kv-moon" style={{ left: '15%' }}>🌙</span>
          </>
        );
      case 'night':
        return <span className="kv-moon">🌙</span>;
      case 'magical':
        return (
          <div className="kv-celestial-dual">
            <span className="kv-sun">☀️</span>
            <span className="kv-moon">🌙</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Render landscape based on biome
  const renderLandscape = () => {
    switch (biome) {
      case 'forest':
        return (
          <div className="kv-landscape">
            <div className="kv-trees-bg">
              {['🌲', '🌳', '🌲', '🌳', '🌲'].map((t, i) => (
                <span key={i}>{t}</span>
              ))}
            </div>
            <div className="kv-trees-fg">
              {['🌳', '🌲', '🌳'].map((t, i) => (
                <span key={i}>{t}</span>
              ))}
            </div>
          </div>
        );
      case 'plains':
        return (
          <div className="kv-landscape">
            <div className="kv-hills">
              <div className="kv-hill"></div>
              <div className="kv-hill"></div>
              <div className="kv-hill"></div>
            </div>
            <div className="kv-grass">
              {['🌸', '🌼', '🌻', '🌺', '🌸'].map((f, i) => (
                <span key={i}>{f}</span>
              ))}
            </div>
          </div>
        );
      case 'riverside':
        return (
          <div className="kv-landscape">
            <div className="kv-river">
              {[...Array(5)].map((_, i) => (
                <span 
                  key={i} 
                  className="kv-river-sparkle"
                  style={{ left: `${20 + i * 15}%`, top: `${30 + Math.random() * 40}%`, animationDelay: `${i * 0.4}s` }}
                >✨</span>
              ))}
            </div>
          </div>
        );
      case 'coastal':
        return (
          <div className="kv-landscape">
            <div className="kv-ocean">
              <div className="kv-wave"></div>
              <div className="kv-wave"></div>
              <div className="kv-wave"></div>
              <span className="kv-ship">⛵</span>
            </div>
          </div>
        );
      case 'mountain':
        return (
          <div className="kv-landscape">
            <div className="kv-mountains">
              <span className="kv-mountain">🏔️</span>
              <span className="kv-mountain">⛰️</span>
              <span className="kv-mountain">🏔️</span>
            </div>
          </div>
        );
      case 'magical':
        return (
          <div className="kv-landscape">
            <div className="kv-magic-glow"></div>
            <div className="kv-crystals">
              <span className="kv-crystal">💎</span>
              <span className="kv-crystal">🔮</span>
              <span className="kv-crystal">💎</span>
            </div>
          </div>
        );
      case 'floating':
        return (
          <div className="kv-landscape">
            <div className="kv-cloud-floor"></div>
            <div className="kv-floating-islands">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="kv-island">
                  <span className="kv-island-top">{['🏰', '🌳', '🏛️'][i]}</span>
                  <span className="kv-island-base">🪨</span>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Get building tier class
  const getBuildingClass = () => {
    if (tier >= 10) return 'tier-max';
    if (tier >= 7) return 'tier-high';
    return '';
  };

  return (
    <div className="kingdom-visual">
      {/* Sky */}
      <div className={`kv-sky ${time}`}>
        {renderCelestial()}
      </div>

      {/* Weather */}
      {renderWeather()}

      {/* Landscape */}
      {renderLandscape()}

      {/* Ground */}
      <div className={`kv-ground ${biome}`}></div>

      {/* Buildings */}
      <div className={`kv-buildings ${getBuildingClass()}`}>
        {buildings.map((b, i) => (
          <span key={i} className="kv-building">{b}</span>
        ))}
      </div>

      {/* Title Banner at TOP */}
      <div className="kv-title-banner">
        <span className="kv-title">{name}</span>
      </div>

      {/* Info Bar at BOTTOM - just stats */}
      <div className="kv-info">
        <div className="kv-tier-badge">
          <span className="kv-tier-icon">{TIER_ICONS[tier] || '🏕️'}</span>
          <span className="kv-tier-text">Tier {tier}</span>
        </div>
        <div className="kv-stats">
          <div className="kv-stat">
            <span className="kv-stat-icon">👥</span>
            <span className="kv-stat-value">{population}</span>
          </div>
          <div className="kv-stat">
            <span className="kv-stat-icon">🏗️</span>
            <span className="kv-stat-value">{totalBuildings}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KingdomVisual;
